import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuBuilding2, LuFileSpreadsheet, LuFolderKanban, LuUsers } from 'react-icons/lu';
import DataCardList from '../components/data/DataCardList';
import DataTable from '../components/data/DataTable';
import DataToolbar from '../components/data/DataToolbar';
import FilterSheet from '../components/data/FilterSheet';
import PageHero from '../components/data/PageHero';
import StatTiles from '../components/data/StatTiles';
import TablePagination from '../components/data/TablePagination';
import ToolbarSelect from '../components/data/ToolbarSelect';
import Button from '../components/ui/Button';
import useElementWidth from '../hooks/useElementWidth';
import useMediaQuery from '../hooks/useMediaQuery';
import usePagedList from '../hooks/usePagedList';
import useTableColumns from '../hooks/useTableColumns';
import useViewportFit from '../hooks/useViewportFit';
import ProjectVisitsModal from './ProfilePage/ProjectVisitsModal';
import useEmployeeReports from './EmployeeReports/useEmployeeReports';
import useEmployeeReportText from './EmployeeReports/employeeReportText';
import { MIN_COLUMN_WIDTH, PINNED_COLUMNS, REPORT_COLUMNS, columnKeysFor, getFilterState } from './EmployeeReports/employeeReportColumns';
import AssignmentCell, { AssignmentCardRow, useAssignmentText } from './EmployeeReports/parts/AssignmentCell';
import AssignmentDetailPane from './EmployeeReports/parts/AssignmentDetailPane';

const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Çalışan Raporları (Tailwind): which employee works on which project and
 * center, with role, tariff (admins) and assignment date. Project / center
 * choice, header filters, a table card that reaches at most the bottom of the
 * screen and the assignment's panel with its visits. Logic: useEmployeeReports.
 */
const EmployeeReports = () => {
  const {
    isAdmin, isLoading, visibleCenters, visibleProjects, selectedProject, setSelectedProject, selectedCenter, setSelectedCenter,
    searchTerm, setSearchTerm, filters, handleFilter, clearFilters, rows, filteredRows, options, stats,
    selectedRow, setSelectedRowId, workerRows, hasVisits, downloadExcel,
  } = useEmployeeReports();
  const { language, locale, label } = useEmployeeReportText();
  const cellText = useAssignmentText();

  const allKeys = useMemo(() => columnKeysFor(isAdmin), [isAdmin]);
  const columnDefs = useMemo(() => Object.fromEntries(allKeys.map((key) => [key, REPORT_COLUMNS[key]])), [allKeys]);
  const columns = useTableColumns({
    storageKey: isAdmin ? 'employeeReports.tableColumns.admin.v1' : 'employeeReports.tableColumns.v1',
    columns: columnDefs,
    defaultVisible: Object.fromEntries(allKeys.map((key) => [key, true])),
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [visitsFor, setVisitsFor] = useState(null);
  const [checkingVisits, setCheckingVisits] = useState(false);
  const hasLoaded = rows.length > 0 || !isLoading;

  const resetKey = JSON.stringify([filters, searchTerm, selectedProject, selectedCenter]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(filteredRows, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  useEffect(() => {
    if (!selectedRow) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedRowId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedRow, showFilterSheet, setSelectedRowId]);

  const columnLabel = useCallback((key) => label(`columns.${key}`), [label]);
  const filterOptions = useMemo(() => ({
    center: options.center.map((value) => ({ value, label: value })),
    project: options.project.map((value) => ({ value, label: value })),
    role: options.role.map((value) => ({ value, label: value })),
  }), [options]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: { type: REPORT_COLUMNS[key].type, options: filterOptions[key] },
  })), [columns.visibleKeys, columnLabel, filterOptions]);

  const filterChips = Object.keys(filters)
    .filter((key) => columnDefs[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: String(filters[key]), state: 'applied' }));

  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm) || Boolean(selectedProject) || Boolean(selectedCenter);

  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedRow ? filteredRows.findIndex((row) => row.id === selectedRow.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredRows[selectedIndex + offset];
    if (!sibling) return;
    setSelectedRowId(sibling.id);
    reveal(sibling.id);
  };

  const openVisits = async () => {
    if (!selectedRow) return;
    setCheckingVisits(true);
    const ok = await hasVisits(selectedRow);
    setCheckingVisits(false);
    if (ok) setVisitsFor(selectedRow);
  };

  const tiles = [
    { key: 'centers', label: label('stats.centers'), shortLabel: label('stats.centersShort'), value: stats.centers, icon: LuBuilding2, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30' },
    { key: 'projects', label: label('stats.projects'), shortLabel: label('stats.projectsShort'), value: stats.projects, icon: LuFolderKanban, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30' },
    { key: 'workers', label: isFilterActive ? label('stats.workersFiltered') : label('stats.workers'), shortLabel: label('stats.workersShort'), value: stats.workers, hint: label('stats.assignments', { count: stats.assignments }), icon: LuUsers, gradient: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30', onClick: clearFilters, active: !isFilterActive },
  ];

  const choices = (
    <>
      <ToolbarSelect
        label={label('toolbar.project')}
        icon={LuFolderKanban}
        value={selectedProject}
        idleValue=""
        onChange={setSelectedProject}
        options={[{ value: '', label: label('toolbar.allProjects') }, ...visibleProjects.map((project) => ({ value: project.id, label: project.titleShort || project.id }))]}
      />
      <ToolbarSelect
        label={label('toolbar.center')}
        icon={LuBuilding2}
        value={selectedCenter}
        idleValue=""
        onChange={setSelectedCenter}
        options={[{ value: '', label: label('toolbar.allCenters') }, ...visibleCenters.map((center) => ({ value: center.id, label: [center.Adı, center.İl].filter(Boolean).join(' - ') }))]}
      />
    </>
  );

  const paneProps = {
    row: selectedRow,
    otherRows: selectedRow ? workerRows(selectedRow.workerId) : [],
    isAdmin,
    checkingVisits,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredRows.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredRows.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedRowId(null),
    onVisits: openVisits,
    onSelectRow: (row) => {
      setSelectedRowId(row.id);
      reveal(row.id);
    },
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <PageHero
          icon={LuUsers}
          gradient="tw-from-sky-400 tw-to-indigo-600 tw-shadow-indigo-500/30"
          title={label('page.title')}
          subtitle={label('page.subtitle')}
          actionsClassName="tw-flex tw-items-center tw-gap-2 max-sm:tw-w-full"
        >
          <Button onClick={downloadExcel} disabled={filteredRows.length === 0} title={label('actions.excelHint')} className="max-sm:tw-w-full">
            <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
            {label('actions.excel')}
          </Button>
        </PageHero>

        <StatTiles tiles={tiles} ready={hasLoaded} />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              columnMenu={{
                columns: allKeys.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
              extra={choices}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(row) => <AssignmentCardRow row={row} />}
                selectedId={selectedRow?.id}
                onSelect={(row) => setSelectedRowId(row.id)}
                isLoading={isLoading}
                isBusy={false}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(row, key) => <AssignmentCell columnKey={key} row={row} />}
                getCellTitle={(row, key) => cellText(row, key) || undefined}
                selectedId={selectedRow?.id}
                onSelect={(row) => setSelectedRowId(row.id)}
                isLoading={isLoading}
                isBusy={false}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={filteredRows.length}
              rangeOf={label('table.rangeOf', { total: filteredRows.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: rows.length.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={false}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <AssignmentDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <AssignmentDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={allKeys.map((key) => ({ key, label: columnLabel(key), type: REPORT_COLUMNS[key].type, options: filterOptions[key] }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredRows.length}
      />

      {visitsFor && (
        <ProjectVisitsModal
          show
          onHide={() => setVisitsFor(null)}
          project={{ id: visitsFor.projectId, title: visitsFor.projectTitle }}
          employeeId={visitsFor.workerId}
          employeeName={visitsFor.fullName}
        />
      )}
    </>
  );
};

export default EmployeeReports;
